/**
 * INVESTOR REEL — full-screen auto-cycle through every hologram with
 * kinetic typography. Designed as a cold-open for VC meetings.
 *
 * UX:
 *   • Plays muted (autoplay works in all browsers); user can click the
 *     pill to unmute and hear the demo score.
 *   • Each clip plays for ~7s (or until ended) then crossfades to the next.
 *   • Big artist name + venue tag + tagline overlay with staggered fade.
 *   • SPACE → next, ← → previous, ESC → exit.
 *   • Auto-loops forever.
 */
import { useEffect, useRef, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Volume2, VolumeX, SkipForward } from "lucide-react";
import SetlistPlayer from "@/components/SetlistPlayer";
import { mediaUrl } from "@/lib/http";

const CLIP_MS = 7000; // each hologram on-screen ~7 sec

export default function InvestorReel({ open, onClose, shows }) {
  const reel = useMemo(
    () => (shows || []).filter((s) => s.hologram_video_url),
    [shows],
  );
  const [idx, setIdx] = useState(0);
  // Start UNMUTED — the click that opened the reel counts as the autoplay
  // user-gesture, so browsers will allow the YouTube setlist / audio fallback
  // to play immediately. If the browser still blocks it (rare on iOS Safari),
  // we render a "Tap to enable sound" overlay below.
  const [muted, setMuted] = useState(false);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const vidRef = useRef(null);

  // Reset on every open. We also reset audioBlocked so the user gets a
  // fresh chance to unblock if they reopen the reel.
  useEffect(() => {
    if (open) {
      setIdx(0);
      setMuted(false);
      setAudioBlocked(false);
    }
  }, [open]);

  // Auto-advance timer
  useEffect(() => {
    if (!open || reel.length === 0) return;
    const t = setTimeout(() => setIdx((i) => (i + 1) % reel.length), CLIP_MS);
    return () => clearTimeout(t);
  }, [open, idx, reel.length]);

  // Keyboard controls
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { onClose(); }
      else if (e.key === " " || e.key === "ArrowRight") { e.preventDefault(); setIdx((i) => (i + 1) % Math.max(1, reel.length)); }
      else if (e.key === "ArrowLeft") { setIdx((i) => (i - 1 + reel.length) % Math.max(1, reel.length)); }
      else if (e.key.toLowerCase() === "m") { setMuted((m) => !m); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, reel.length]);

  if (!open) return null;

  if (reel.length === 0) {
    return (
      <div className="fixed inset-0 z-[200] bg-black flex items-center justify-center" data-testid="investor-reel-empty">
        <div className="text-center max-w-md">
          <p className="font-mono-ui text-[11px] tracking-[0.32em] uppercase text-[#FF007F] mb-4">No holograms generated yet</p>
          <button onClick={onClose} className="btn-neon" data-testid="investor-reel-close-empty">Back</button>
        </div>
      </div>
    );
  }

  const show = reel[idx];
  const videoUrl = mediaUrl(show.hologram_video_url);

  // Real-song audio: YouTube setlist FIRST (real famous tracks), then a
  // single YouTube URL fallback. The Hedra MP4's baked-in demo-score audio
  // is muted on the video element below, and the show's audio_url (demo
  // score MP3) is intentionally NOT used here — investors hear the real
  // songs, never the demo placeholders.
  const setlist = Array.isArray(show.setlist) && show.setlist.length > 0 ? show.setlist : [];
  const ytFallback = show.youtube_url || null;
  const hasYouTube = setlist.length > 0 || !!ytFallback;
  const audioUrl = null; // demo score MP3 removed per user request

  return (
    <div
      className="fixed inset-0 z-[200] bg-black overflow-hidden"
      data-testid="investor-reel"
      onClick={() => setIdx((i) => (i + 1) % reel.length)}
    >
      {/* The hologram video — fades in on each change */}
      <AnimatePresence mode="wait">
        <motion.video
          key={show.id}
          ref={vidRef}
          src={videoUrl}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.97 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="investor-reel-video"
        />
      </AnimatePresence>

      {/* REAL-SONG AUDIO — invisible YouTube (preferred) or MP3 audio tag.
          Hidden off-screen but emits sound when unmuted. We re-mount on every
          show change so the next artist's track always starts fresh. */}
      {hasYouTube ? (
        <div
          key={`yt-${show.id}`}
          aria-hidden="true"
          style={{ position: "absolute", left: -9999, top: -9999, width: 320, height: 180, pointerEvents: "none" }}
        >
          <SetlistPlayer
            setlist={setlist}
            fallbackUrl={ytFallback}
            muted={muted}
            paused={false}
            testId="investor-reel-yt"
          />
        </div>
      ) : audioUrl ? (
        <audio
          key={`audio-${show.id}`}
          ref={(el) => {
            if (!el) return;
            // Try autoplay unmuted. If the browser rejects (iOS Safari often
            // does on the FIRST play even after a user-gesture if there was
            // no audible track yet), fall back to muted autoplay and surface
            // a "Tap to enable sound" prompt.
            const tryPlay = async () => {
              try {
                el.muted = muted;
                await el.play();
              } catch (e) {
                el.muted = true;
                try { await el.play(); } catch {}
                if (!muted) setAudioBlocked(true);
              }
            };
            tryPlay();
          }}
          src={audioUrl}
          autoPlay
          loop
          muted={muted}
          data-testid="investor-reel-audio"
          style={{ position: "absolute", left: -9999, top: -9999 }}
        />
      ) : null}

      {/* Audio-blocked prompt — appears if the browser refused autoplay-with-sound.
          One tap anywhere unblocks future audio for the rest of the reel. */}
      {audioBlocked && (
        <div
          className="absolute top-20 left-1/2 -translate-x-1/2 z-20 pointer-events-auto"
          onClick={(e) => {
            e.stopPropagation();
            setAudioBlocked(false);
            setMuted(false);
            // Force-play all <audio> elements currently mounted
            document.querySelectorAll('[data-testid="investor-reel-audio"]').forEach((a) => {
              try { a.muted = false; a.play(); } catch {}
            });
          }}
          data-testid="investor-reel-unblock"
        >
          <button className="inline-flex items-center gap-2 border border-[#00E5FF] bg-[#00E5FF] text-black font-mono-ui text-[11px] tracking-[0.28em] uppercase font-bold py-3 px-5 hover:bg-white transition-colors animate-pulse">
            <Volume2 size={14} /> Tap to enable sound
          </button>
        </div>
      )}

      {/* Hologram veil + scanlines for that "real holographic" look */}
      <div className="absolute inset-0 hologram-cyan-veil pointer-events-none" />
      <div className="absolute inset-0 hologram-scanlines-overlay pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-black/80 pointer-events-none" />

      {/* Emitter pad glow */}
      <div
        className="absolute pointer-events-none"
        style={{
          left: "50%", bottom: "8%", width: "44%", height: "5%",
          transform: "translateX(-50%)",
          background: "radial-gradient(ellipse at center, rgba(160,255,255,0.85) 0%, rgba(60,200,255,0.5) 30%, rgba(255,80,170,0.25) 65%, rgba(0,0,0,0) 100%)",
          filter: "blur(3px)", mixBlendMode: "screen",
        }}
      />

      {/* Top bar — tagline + clip counter */}
      <div className="absolute top-0 inset-x-0 p-6 md:p-10 flex items-start justify-between gap-6 pointer-events-none">
        <div>
          <div className="font-mono-ui text-[10px] md:text-xs tracking-[0.4em] uppercase text-[#00E5FF] inline-flex items-center gap-2">
            <span className="ring-dot" /> HOLOGRAM CLUB · INVESTOR REEL
          </div>
          <div className="mt-2 font-mono-ui text-[10px] md:text-xs tracking-[0.28em] uppercase text-white/55 max-w-xl">
            A 24/7 volumetric concert venue · Las Vegas + Los Angeles · resurrecting the icons in photonic light.
          </div>
        </div>
        <div className="font-mono-ui text-[10px] md:text-xs tracking-[0.28em] uppercase text-white/60 text-right">
          <div>{String(idx + 1).padStart(2, "0")} / {String(reel.length).padStart(2, "0")}</div>
          <div className="text-[#FF007F] mt-1">{(show.venue || "vegas").toUpperCase()}</div>
        </div>
      </div>

      {/* Artist name — staggered fade per clip */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`label-${show.id}`}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.55, ease: "easeOut", delay: 0.1 }}
          className="absolute left-6 md:left-12 bottom-24 md:bottom-32 max-w-3xl pointer-events-none"
          data-testid="investor-reel-label"
        >
          <div className="font-mono-ui text-[10px] md:text-xs tracking-[0.32em] uppercase text-[#00E5FF]">
            {show.slot} · {show.era}
          </div>
          <div className="mt-2 md:mt-3 font-display font-black tracking-tighter text-6xl md:text-8xl lg:text-9xl leading-[0.85] text-white drop-shadow-[0_0_30px_rgba(0,229,255,0.45)]">
            {show.artist}
          </div>
          {show.tagline && (
            <p className="mt-4 md:mt-6 text-white/75 text-sm md:text-base max-w-xl">
              {show.tagline}
            </p>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Progress bar — one tick per clip */}
      <div className="absolute bottom-0 inset-x-0 px-6 md:px-12 pb-6 md:pb-8 pointer-events-none">
        <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${reel.length}, minmax(0, 1fr))` }}>
          {reel.map((_, i) => (
            <div key={i} className="h-[3px] bg-white/15 overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-[#00E5FF] to-[#FF007F]"
                initial={false}
                animate={{ width: i < idx ? "100%" : i === idx ? "100%" : "0%" }}
                transition={{
                  duration: i === idx ? CLIP_MS / 1000 : 0,
                  ease: "linear",
                }}
              />
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between font-mono-ui text-[10px] tracking-[0.32em] uppercase text-white/55">
          <span>Press <span className="text-white">SPACE</span> to skip · <span className="text-white">M</span> to mute · <span className="text-white">ESC</span> to exit</span>
          <span className="text-[#00E5FF]">holigram-club</span>
        </div>
      </div>

      {/* Controls — top right */}
      <div className="absolute top-6 right-6 md:top-10 md:right-10 flex items-center gap-3 z-10">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setMuted((m) => !m); }}
          className="w-11 h-11 rounded-full border border-white/25 bg-black/60 backdrop-blur flex items-center justify-center text-white/80 hover:text-white hover:border-[#00E5FF] transition-colors"
          data-testid="investor-reel-mute"
          title={muted ? "Unmute (M)" : "Mute (M)"}
        >
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setIdx((i) => (i + 1) % reel.length); }}
          className="w-11 h-11 rounded-full border border-white/25 bg-black/60 backdrop-blur flex items-center justify-center text-white/80 hover:text-white hover:border-[#FF007F] transition-colors"
          data-testid="investor-reel-skip"
          title="Next (SPACE)"
        >
          <SkipForward size={16} />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="w-11 h-11 rounded-full border border-white/30 bg-black/60 backdrop-blur flex items-center justify-center text-white/85 hover:text-white hover:border-[#FF007F] transition-colors"
          data-testid="investor-reel-close"
          title="Exit reel (ESC)"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
